<script setup lang="ts">
import { onUnmounted, watch } from "vue";

import { getBoardState } from "@/backend/read/BackendReader";
import { addSticky, changeSticky, withExplicitDefaults } from "@/backend/read/changeSticky";
import SidePanelContent from "@/components/SidePanel/SidePanelContent.vue";
import ComplexList from "@/components/ui/ComplexList/ComplexList.vue";
import ListEmpty from "@/components/ui/ComplexList/ListEmpty.vue";
import LoadingIndicator from "@/components/ui/LoadingIndicator.vue";
import { runMinDelay, useSyncedTask } from "@/composables/useSyncedTask";
import { useActivityStore } from "@/store/activity";
import { useBoardStore } from "@/store/board";

const store = useActivityStore();
const { loading, runSync } = useSyncedTask();

onUnmounted(() => {
  void loadData(new Date());
});

watch(
  () => ({ ...store.allFilters }),
  (newState, prevState) => {
    if (newState.date.fromDate !== prevState?.date.fromDate) {
      void loadData(newState.date.fromDate);
    }
  },
  { immediate: true },
);

async function loadData(date: Date) {
  const board = useBoardStore().currentBoard();
  await runSync(
    () => runMinDelay(getBoardState(board.id, date), 300),
    (stickies) => {
      const toDelete = new Set(Object.keys(board.cards));
      for (const sticky of stickies) {
        toDelete.delete(sticky.id);
        if (sticky.id in board.cards) {
          changeSticky(board, sticky.id, withExplicitDefaults(sticky));
        } else {
          addSticky(sticky);
        }
      }
      for (const id of toDelete) {
        delete board.cards[id];
      }
    },
  );
}
</script>

<template>
  <SidePanelContent v-focus-trap v-autofocus>
    <template #sub-title>
      <div class="panel-header">
        <h2 class="subtitle">{{ $t("boardDiff.visualize.title") }}</h2>
        <p class="description">{{ $t("boardDiff.visualize.description") }}</p>
      </div>
    </template>

    <ComplexList>
      <ListEmpty v-if="loading">
        <LoadingIndicator />
      </ListEmpty>
    </ComplexList>
  </SidePanelContent>
</template>
